import * as React from "react";
import { useCallback, useMemo, useRef } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { TrashIcon, type TrashIconHandle } from "@/components/ui/trash";
import { cn } from "@/lib/utils";

/** Matches toolbar row actions (archive / restore) at 2.25rem. */
export const TABLE_OUTLINE_DELETE_ICON_BUTTON_CLASS = "size-[2.25rem] shrink-0 p-0";

export type TableOutlineDeleteMarker =
  | "reports"
  | "strategies"
  | "simulator"
  | "strategyRd";

const MARKER_ATTR: Record<TableOutlineDeleteMarker, Record<string, string>> = {
  reports: { "data-reports-table-delete": "" },
  strategies: { "data-strategies-table-delete": "" },
  simulator: { "data-simulator-list-delete": "" },
  strategyRd: { "data-strategy-rd-library-delete": "" },
};

export type TableOutlineDeleteIconButtonProps = {
  "aria-label": string;
  disabled?: boolean;
  loading?: boolean;
  marker: TableOutlineDeleteMarker;
  className?: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export function TableOutlineDeleteIconButton({
  "aria-label": ariaLabel,
  disabled = false,
  loading = false,
  marker,
  className,
  onClick,
}: TableOutlineDeleteIconButtonProps): React.ReactElement {
  const trashIconRef = useRef<TrashIconHandle | null>(null);

  const startTrashIconAnimation = useCallback(() => {
    if (loading || disabled) return;
    void trashIconRef.current?.startAnimation();
  }, [disabled, loading]);

  const stopTrashIconAnimation = useCallback(() => {
    void trashIconRef.current?.stopAnimation();
  }, []);

  const trashIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startTrashIconAnimation,
      onMouseEnter: startTrashIconAnimation,
      onMouseLeave: stopTrashIconAnimation,
    }),
    [startTrashIconAnimation, stopTrashIconAnimation]
  );

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      data-font="primary"
      data-trade-log-action="delete"
      {...MARKER_ATTR[marker]}
      className={cn(TABLE_OUTLINE_DELETE_ICON_BUTTON_CLASS, className)}
      aria-label={ariaLabel}
      disabled={disabled || loading}
      onClick={(event) => {
        event.stopPropagation();
        onClick(event);
      }}
      {...trashIconHoverHandlers}
    >
      {loading ? (
        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
      ) : (
        <TrashIcon
          ref={trashIconRef}
          size={16}
          className="shrink-0 text-current"
          aria-hidden
        />
      )}
    </Button>
  );
}
